import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SntRadio from "@cleanup/components/SntRadio/SntRadio";
import useOuterClick from "@cleanup/hooks/useOutsideElement";
import ExcludeLabel from "@cleanup/components/AdvancedFilters/ExcludeLabel";
import GeneralFooterFilter from "@cleanup/components/GeneralFooterFilter/GeneralFooterFilter";
import * as Yup from "yup";
import { Formik } from "formik";
import StringUtils from "@/utils/StringUtils";
import { AdvanceFilter, FilterBody, FilterHeaderStyle } from "@cleanup/components/AdvancedFilters/FilterStyle";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import SntCloseSmallIcon from "@icons/SntCloseSmallIcon";
import SntArrowDownIcon from "@icons/SntArrowDownIcon";
import usePositionFilter from "@cleanup/hooks/usePositionFilter";
import SntSelect from "@cleanup/wrappers/ReactSelect/SntSelect";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface FilterTypeSettings {
  unit?: string;
  supportDurationUnits?: string[];
}

interface Descriptor {
  _idx: string;
  label?: string;
  description: string;
  filterTypeSettings: FilterTypeSettings;
}

interface FilterValue {
  operator?: string;
  eq?: string | number;
  lt?: string | number;
  gt?: string | number;
  durationUnit?: string;
}

interface FilterData {
  filterValue?: FilterValue;
  notFilter?: boolean;
  idx?: string;
}

interface NumericFilterProps {
  descriptor: Descriptor;
  data?: FilterData;
  onChange?: (data: FilterData | { idx: string; data: null }) => void;
  disabled?: boolean;
}

interface InitialValues {
  smallerVal: string;
  greaterVal: string;
  betweenGreaterVal: string;
  betweenSmallerVal: string;
  equalVal?: string;
}

const NumericFilter: React.FC<NumericFilterProps> = ({
  descriptor,
  data = {},
  onChange,
  disabled = false,
}) => {
  const [isShow, setShow] = useState(false);
  const [title, setTitle] = useState("");
  const [errors, setErrors] = useState<Record<string, unknown> | null>(null);
  const [selectedValue, setSelectedValue] = useState("");
  const [notFilter, setNotFilter] = useState(false);
  const [initialValues, setInitialValues] = useState<InitialValues>({
    smallerVal: "1",
    greaterVal: "0",
    betweenGreaterVal: "1",
    betweenSmallerVal: "0",
  });

  const popupStyleRef = useRef<Record<string, unknown>>({});
  const { popupRef, buttonRef, getPosition } = usePositionFilter();

  const validationSchema = Yup.object({
    smallerVal: Yup.number().required(translate(LanguageKey.valid_required_key)),
    greaterVal: Yup.number().required(translate(LanguageKey.valid_required_key)),
    betweenGreaterVal: Yup.number()
      .required(StringUtils.replaceStr(translate(LanguageKey.greater_than_key), "0"))
      .label("The second value")
      .test({
        test: function (value) {
          const toValue = this.resolve(Yup.ref("betweenSmallerVal"));
          return value && value >= toValue
            ? true
            : this.createError({
                message: `The second value must be greater than or equal to ${toValue}`,
                path: "betweenGreaterVal",
              });
        },
      }),
    betweenSmallerVal: Yup.number()
      .required(StringUtils.replaceStr(translate(LanguageKey.greater_than_key), "0"))
      .label("The first value"),
  });

  const getDefaultUnit = () => {
    return (
      (descriptor.filterTypeSettings.supportDurationUnits &&
        descriptor.filterTypeSettings.supportDurationUnits[0]) ||
      ""
    );
  };
  const [selectedUnit, setSelectedUnit] = useState(getDefaultUnit());

  useEffect(() => {
    let filterValue: FilterValue = {};
    if (data) {
      filterValue = data.filterValue || {};
    }
    let _title = translate(LanguageKey.all_key);
    let operator = "";
    let unit = getDefaultUnit();
    if (filterValue && filterValue.durationUnit) {
      unit = filterValue.durationUnit;
    }
    setSelectedUnit(unit);
    let unitDisplay = unit;
    if (!unitDisplay) {
      unitDisplay = descriptor.filterTypeSettings.unit || "";
    } else {
      const unitKey = `${unit.toLowerCase()}_key`;
      unitDisplay = translate(unitKey as LanguageKey) || unit;
    }
    if (filterValue) {
      operator = filterValue.operator || "";
      if (operator === "EQ") {
        _title = "= " + filterValue.eq + " " + unitDisplay;
      } else if (operator === "NULL") {
        _title = translate(LanguageKey.has_no_value_key) + unitDisplay;
      } else if (operator === "LT") {
        _title = "< " + filterValue.lt + " " + unitDisplay;
      } else if (operator === "GT") {
        _title = "> " + filterValue.gt + " " + unitDisplay;
      } else if (operator === "BETWEEN") {
        _title = StringUtils.replaceStr(
          translate(LanguageKey.between_and_key),
          String(filterValue.lt),
          filterValue.gt + " " + unitDisplay
        );
      }

      if (operator) {
        if (operator === "EQ") {
          setInitialValues({ ...initialValues, equalVal: String(filterValue.eq) });
        }

        if (operator === "LT") {
          setInitialValues({ ...initialValues, smallerVal: String(filterValue.lt) });
        }

        if (operator === "GT") {
          setInitialValues({ ...initialValues, greaterVal: String(filterValue.gt) });
        }

        if (operator === "BETWEEN") {
          setInitialValues({
            ...initialValues,
            betweenGreaterVal: String(filterValue.lt),
            betweenSmallerVal: String(filterValue.gt),
          });
        }
      }
    }
    setSelectedValue(operator);
    setNotFilter(Boolean(data.notFilter));

    setTitle(_title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, data.filterValue, descriptor]);

  const getData = useCallback(() => {
    const newData = JSON.parse(JSON.stringify(data)) as FilterData;
    if (selectedValue === "EQ") {
      newData.filterValue = {
        operator: "EQ",
        eq: initialValues.equalVal,
        durationUnit: selectedUnit,
      };
    } else if (selectedValue === "NULL") {
      newData.filterValue = {
        operator: "NULL",
        durationUnit: selectedUnit,
      };
    } else if (selectedValue === "LT") {
      newData.filterValue = {
        operator: "LT",
        lt: initialValues.smallerVal,
        durationUnit: selectedUnit,
      };
    } else if (selectedValue === "GT") {
      newData.filterValue = {
        operator: "GT",
        gt: initialValues.greaterVal,
        durationUnit: selectedUnit,
      };
    } else if (selectedValue === "BETWEEN") {
      newData.filterValue = {
        operator: "BETWEEN",
        gt: initialValues.betweenSmallerVal,
        lt: initialValues.betweenGreaterVal,
        durationUnit: selectedUnit,
      };
    }
    newData.notFilter = notFilter;
    return newData;
  }, [data, selectedValue, notFilter, initialValues, selectedUnit]);

  const isChanged = useCallback(() => {
    const oldDataFilter = data.filterValue || {};
    const newData = getData();
    const newDataFilter = newData.filterValue || {};

    if (
      oldDataFilter.eq === newDataFilter.eq &&
      oldDataFilter.gt === newDataFilter.gt &&
      oldDataFilter.lt === newDataFilter.lt &&
      oldDataFilter.operator === newDataFilter.operator &&
      oldDataFilter.durationUnit === newDataFilter.durationUnit &&
      (data.notFilter === newData.notFilter ||
        (data.notFilter === undefined && newData.notFilter === false) ||
        (data.notFilter === false && newData.notFilter === undefined))
    ) {
      return false;
    }
    return true;
  }, [data.filterValue, data.notFilter, getData]);

  const refWrapper = useOuterClick((e) => {
    if (
      e &&
      e.target &&
      (e.target as HTMLElement).closest &&
      (e.target as HTMLElement).closest('[id*="react-select"]')
    ) {
      return;
    }
    if (isShow && errors && Object.keys(errors).length === 0) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    }
  });

  const onClickButton = () => {
    if (isShow) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    } else {
      popupStyleRef.current = getPosition();
      setShow(true);
    }
  };

  const onAppliedFilter = () => {
    setShow(false);
    if (isChanged()) {
      if (onChange) {
        onChange(getData());
      }
    }
  };

  const onClearFilter = () => {
    setSelectedValue("");
    setNotFilter(false);
    setShow(false);
    if (onChange) {
      onChange({
        idx: descriptor._idx,
        data: null,
      });
    }
  };

  const excludeLabel = useMemo(() => {
    return data.notFilter ? <ExcludeLabel /> : null;
  }, [data.notFilter]);

  return (
    <AdvanceFilter ref={refWrapper}>
      <Button
        ref={buttonRef}
        variant="sensolus-greylight"
        title={descriptor.description}
        disabled={disabled}
        onClick={onClickButton}
      >
        {selectedValue && (
          <SntCloseSmallIcon
            className="me-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClearFilter();
            }}
          />
        )}
        <span>{descriptor.label || descriptor.description}</span>:{" "}
        <span>{excludeLabel}</span>
        <FilterHeaderStyle>{title}</FilterHeaderStyle>
        <SntArrowDownIcon />
      </Button>
      <FilterBody
        ref={popupRef}
        style={{ display: isShow ? "block" : "none", ...popupStyleRef.current }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={() => {
            onAppliedFilter();
          }}
        >
          {({ handleSubmit, handleChange, values, errors }) => {
            setTimeout(() => setErrors(errors), 0);
            return (
              <form onSubmit={handleSubmit}>
                <Container>
                  <div className="mb-3 mt-3">
                    <SntRadio
                      value="EQ"
                      selectedValue={selectedValue}
                      onChange={(value) => value !== undefined && setSelectedValue(value)}
                      label={translate(LanguageKey.has_equal_value_key)}
                    />
                  </div>
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-between">
                      <Form.Control
                        name="equalVal"
                        value={values.equalVal}
                        type="number"
                        className={`${
                          errors.equalVal && selectedValue === "EQ"
                            ? "is-invalid"
                            : "is-valid"
                        } me-2`}
                        style={{ position: "relative" }}
                        disabled={selectedValue !== "EQ"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            equalVal: e.target.value,
                          });
                        }}
                      />
                      {(descriptor.filterTypeSettings.unit ||
                        (descriptor.filterTypeSettings.supportDurationUnits &&
                          descriptor.filterTypeSettings.supportDurationUnits
                            .length > 0)) && (
                        <div
                          className="align-self-center"
                          style={{ minWidth: 120 }}
                        >
                          {descriptor.filterTypeSettings.supportDurationUnits &&
                          descriptor.filterTypeSettings.supportDurationUnits
                            .length > 0 ? (
                            <SntSelect
                              value={
                                selectedUnit
                                  ? {
                                      value: selectedUnit,
                                      label:
                                        translate(`${selectedUnit.toLowerCase()}_key` as LanguageKey) || selectedUnit,
                                    }
                                  : null
                              }
                              onChange={(option: { value: string; label: string } | null) =>
                                setSelectedUnit(option?.value || "")
                              }
                              options={(
                                descriptor.filterTypeSettings
                                  .supportDurationUnits || []
                              ).map((u) => ({
                                value: u,
                                label: translate(`${u.toLowerCase()}_key` as LanguageKey) || u,
                              }))}
                              isDisabled={selectedValue !== "EQ"}
                            />
                          ) : (
                            <span>{descriptor.filterTypeSettings.unit}</span>
                          )}
                        </div>
                      )}
                    </Col>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{
                        display:
                          errors.smallerVal && selectedValue === "EQ"
                            ? "block"
                            : "none",
                      }}
                    >
                      {errors.smallerVal as string}
                    </Form.Control.Feedback>
                  </Row>

                  <div className="mb-3 mt-3">
                    <SntRadio
                      value="LT"
                      selectedValue={selectedValue}
                      onChange={(value) => value !== undefined && setSelectedValue(value)}
                      label={translate(LanguageKey.less_than_key)}
                    />
                  </div>
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-between">
                      <Form.Control
                        name="smallerVal"
                        value={values.smallerVal}
                        type="number"
                        className={`${
                          errors.smallerVal && selectedValue === "LT"
                            ? "is-invalid"
                            : "is-valid"
                        } me-2`}
                        style={{ position: "relative" }}
                        disabled={selectedValue !== "LT"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            smallerVal: e.target.value,
                          });
                        }}
                      />
                      {(descriptor.filterTypeSettings.unit ||
                        (descriptor.filterTypeSettings.supportDurationUnits &&
                          descriptor.filterTypeSettings.supportDurationUnits
                            .length > 0)) && (
                        <div
                          className="align-self-center"
                          style={{ minWidth: 120 }}
                        >
                          {descriptor.filterTypeSettings.supportDurationUnits &&
                          descriptor.filterTypeSettings.supportDurationUnits
                            .length > 0 ? (
                            <SntSelect
                              value={
                                selectedUnit
                                  ? {
                                      value: selectedUnit,
                                      label:
                                        translate(`${selectedUnit.toLowerCase()}_key` as LanguageKey) || selectedUnit,
                                    }
                                  : null
                              }
                              onChange={(option: { value: string; label: string } | null) =>
                                setSelectedUnit(option?.value || "")
                              }
                              options={(
                                descriptor.filterTypeSettings
                                  .supportDurationUnits || []
                              ).map((u) => ({
                                value: u,
                                label: translate(`${u.toLowerCase()}_key` as LanguageKey) || u,
                              }))}
                              isDisabled={selectedValue !== "LT"}
                            />
                          ) : (
                            <span>{descriptor.filterTypeSettings.unit}</span>
                          )}
                        </div>
                      )}
                    </Col>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{
                        display:
                          errors.smallerVal && selectedValue === "LT"
                            ? "block"
                            : "none",
                      }}
                    >
                      {errors.smallerVal as string}
                    </Form.Control.Feedback>
                  </Row>

                  <div className="mb-3">
                    <SntRadio
                      value="BETWEEN"
                      selectedValue={selectedValue}
                      onChange={(value) => value !== undefined && setSelectedValue(value)}
                      label={translate(LanguageKey.between_key)}
                    />
                  </div>
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-between">
                      <Form.Control
                        name="betweenSmallerVal"
                        value={values.betweenSmallerVal}
                        type="number"
                        className={`tmp me-2 ${
                          (errors.betweenGreaterVal ||
                            errors.betweenSmallerVal) &&
                          selectedValue === "BETWEEN"
                            ? "is-invalid"
                            : "is-valid"
                        }`}
                        style={{ position: "relative" }}
                        disabled={selectedValue !== "BETWEEN"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            betweenSmallerVal: e.target.value,
                          });
                        }}
                      />
                      <div className="align-self-center me-2">-</div>
                      <Form.Control
                        name="betweenGreaterVal"
                        value={values.betweenGreaterVal}
                        type="number"
                        className={`tmp me-2 ${
                          (errors.betweenGreaterVal ||
                            errors.betweenSmallerVal) &&
                          selectedValue === "BETWEEN"
                            ? "is-invalid"
                            : "is-valid"
                        }`}
                        style={{ position: "relative" }}
                        disabled={selectedValue !== "BETWEEN"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            betweenGreaterVal: e.target.value,
                          });
                        }}
                      />
                      {(descriptor.filterTypeSettings.unit ||
                        (descriptor.filterTypeSettings.supportDurationUnits &&
                          descriptor.filterTypeSettings.supportDurationUnits
                            .length > 0)) && (
                        <div
                          className="align-self-center"
                          style={{ minWidth: 120 }}
                        >
                          {descriptor.filterTypeSettings.supportDurationUnits &&
                          descriptor.filterTypeSettings.supportDurationUnits
                            .length > 0 ? (
                            <SntSelect
                              value={
                                selectedUnit
                                  ? {
                                      value: selectedUnit,
                                      label:
                                        translate(`${selectedUnit.toLowerCase()}_key` as LanguageKey) || selectedUnit,
                                    }
                                  : null
                              }
                              onChange={(option: { value: string; label: string } | null) =>
                                setSelectedUnit(option?.value || "")
                              }
                              options={(
                                descriptor.filterTypeSettings
                                  .supportDurationUnits || []
                              ).map((u) => ({
                                value: u,
                                label: translate(`${u.toLowerCase()}_key` as LanguageKey) || u,
                              }))}
                              isDisabled={selectedValue !== "BETWEEN"}
                            />
                          ) : (
                            <span>{descriptor.filterTypeSettings.unit}</span>
                          )}
                        </div>
                      )}
                    </Col>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{
                        display:
                          (errors.betweenGreaterVal ||
                            errors.betweenSmallerVal) &&
                          selectedValue === "BETWEEN"
                            ? "block"
                            : "none",
                      }}
                    >
                      {(errors.betweenSmallerVal || errors.betweenGreaterVal) as string}
                    </Form.Control.Feedback>
                  </Row>

                  <div className="mb-3">
                    <SntRadio
                      value="GT"
                      selectedValue={selectedValue}
                      onChange={(value) => value !== undefined && setSelectedValue(value)}
                      label={translate(LanguageKey.more_than_key)}
                    />
                  </div>
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-between">
                      <Form.Control
                        name="greaterVal"
                        value={values.greaterVal}
                        type="number"
                        className={`tmp me-2 ${
                          errors.greaterVal && selectedValue === "GT"
                            ? "is-invalid"
                            : "is-valid"
                        }`}
                        style={{ position: "relative" }}
                        disabled={selectedValue !== "GT"}
                        onChange={(e) => {
                          handleChange(e);
                          setInitialValues({
                            ...initialValues,
                            greaterVal: e.target.value,
                          });
                        }}
                      />
                      {(descriptor.filterTypeSettings.unit ||
                        (descriptor.filterTypeSettings.supportDurationUnits &&
                          descriptor.filterTypeSettings.supportDurationUnits
                            .length > 0)) && (
                        <div
                          className="align-self-center"
                          style={{ minWidth: 120 }}
                        >
                          {descriptor.filterTypeSettings.supportDurationUnits &&
                          descriptor.filterTypeSettings.supportDurationUnits
                            .length > 0 ? (
                            <SntSelect
                              value={
                                selectedUnit
                                  ? {
                                      value: selectedUnit,
                                      label:
                                        translate(`${selectedUnit.toLowerCase()}_key` as LanguageKey) || selectedUnit,
                                    }
                                  : null
                              }
                              onChange={(option: { value: string; label: string } | null) =>
                                setSelectedUnit(option?.value || "")
                              }
                              options={(
                                descriptor.filterTypeSettings
                                  .supportDurationUnits || []
                              ).map((u) => ({
                                value: u,
                                label: translate(`${u.toLowerCase()}_key` as LanguageKey) || u,
                              }))}
                              isDisabled={selectedValue !== "GT"}
                            />
                          ) : (
                            <span>{descriptor.filterTypeSettings.unit}</span>
                          )}
                        </div>
                      )}
                    </Col>
                    <Form.Control.Feedback
                      type="invalid"
                      style={{
                        display:
                          errors.greaterVal && selectedValue === "GT"
                            ? "block"
                            : "none",
                      }}
                    >
                      {errors.greaterVal as string}
                    </Form.Control.Feedback>
                  </Row>
                  <div className="mb-3 mt-3">
                    <SntRadio
                      value="NULL"
                      selectedValue={selectedValue}
                      onChange={(value) => value !== undefined && setSelectedValue(value)}
                      label={translate(LanguageKey.has_no_value_key)}
                    />
                  </div>
                </Container>
                <GeneralFooterFilter
                  onClearFilter={() => onClearFilter()}
                  onAppliedFilter={() => {
                    if (Object.keys(errors).length === 0) {
                      handleSubmit();
                    }
                  }}
                  isNotFilter={notFilter}
                  onCheckNotFilter={(e) => {
                    setNotFilter(e);
                  }}
                  disableCheckBox={!selectedValue}
                />
              </form>
            );
          }}
        </Formik>
      </FilterBody>
    </AdvanceFilter>
  );
};

export default NumericFilter;
